// hooks/useTenantLease.ts - Tenant операції з lease через LeaseController
/*
Цей хук покриває:

✅ Мої всі lease (GET /lease/my)
✅ Деталі конкретного lease (GET /lease/:id)
✅ Достроково завершити lease (PATCH /lease/:id/terminate)

Особливості:

Окремі query keys для tenant контексту
Фільтрація по статусу (active/terminated/expired)
Helper функції для частих сценаріїв (активні lease, статистика)
Автоматична інвалідація кешу при змінах
Логіка для tenant dashboard
*/

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getTenantLeaseDetailsRequest,
  getTenantLeasesRequest,
  terminateTenantLeaseRequest,
  getMyLeasesRequest,
  MyLease,
} from '@/api';

// === ТИПИ ===
interface TenantLeaseListItem {
  id: number;
  propertyName: string;
  unitNumber: string;
  monthlyRent: number;
  startDate: string;
  endDate?: string;
  status: 'active' | 'terminated' | 'expired';
  propertyAddress: string;
  landlordName?: string;
}

// === QUERY KEYS ===
export const tenantLeaseKeys = {
  all: ['tenant-lease'] as const,
  list: () => [...tenantLeaseKeys.all, 'list'] as const,
  myLeases: () => [...tenantLeaseKeys.all, 'my-leases'] as const,
  details: (leaseId: number) =>
    [...tenantLeaseKeys.all, 'details', leaseId] as const,
};

// === МОЇ LEASE (СПИСОК) ===

/**
 * Хук для отримання всіх lease tenant'a (активні + історія)
 * GET /lease/my
 */
export const useTenantLeases = (options?: {
  enabled?: boolean;
  filters?: {
    status?: 'active' | 'terminated' | 'expired';
    propertyId?: number;
  };
}) => {
  return useQuery({
    queryKey: tenantLeaseKeys.list(),
    queryFn: () => getTenantLeasesRequest(options?.filters),
    enabled: options?.enabled !== false,
    select: (data) => data.data, // Масив TenantLeaseListItem[]
    staleTime: 5 * 60 * 1000, // 5 хвилин кеш
  });
};

/**
 * Хук для отримання всіх лізингів для поточного тенанта
 * GET /api/lease/my
 */
export const useMyLeases = (options?: { enabled?: boolean }) => {
  return useQuery<MyLease[], Error>({
    queryKey: tenantLeaseKeys.myLeases(),
    queryFn: async () => {
      const response = await getMyLeasesRequest();
      return response.data;
    },
    enabled: options?.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 хвилин кеш
  });
};

// === ДЕТАЛІ КОНКРЕТНОГО LEASE ===

/**
 * Хук для отримання деталей конкретного lease
 * GET /lease/:id
 */
export const useTenantLeaseDetails = (
  leaseId: number | undefined,
  options?: {
    enabled?: boolean;
  },
) => {
  return useQuery({
    queryKey: leaseId ? tenantLeaseKeys.details(leaseId) : [],
    queryFn: () => {
      if (!leaseId) {
        throw new Error('Lease ID is required');
      }
      return getTenantLeaseDetailsRequest(leaseId);
    },
    enabled: !!(leaseId && options?.enabled !== false),
    select: (data) => data.data, // Повний LeaseInfo об'єкт
  });
};

// === ДОСТРОКОВО ЗАВЕРШИТИ LEASE ===

/**
 * Хук для достроково завершення lease tenant'ом
 * PATCH /lease/:id/terminate
 */
export const useTerminateTenantLease = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      leaseId: number;
      reason?: string;
      terminationDate?: string;
    }) => {
      const { leaseId, reason, terminationDate } = params;
      return terminateTenantLeaseRequest(leaseId, {
        reason,
        terminationDate,
      });
    },
    onSuccess: (data, variables) => {
      const { leaseId } = variables;

      // Інвалідуємо список lease tenant'a
      queryClient.invalidateQueries({
        queryKey: tenantLeaseKeys.list(),
      });

      // Інвалідуємо новий список myLeases
      queryClient.invalidateQueries({
        queryKey: tenantLeaseKeys.myLeases(),
      });

      // Інвалідуємо деталі конкретного lease
      queryClient.invalidateQueries({
        queryKey: tenantLeaseKeys.details(leaseId),
      });

      // Також можна інвалідувати кеш на стороні owner/manager
      // якщо tenant і owner використовують один додаток
      queryClient.invalidateQueries({
        queryKey: ['unit-lease'],
      });
      queryClient.invalidateQueries({
        queryKey: ['property-units'],
      });
    },
    onError: (error) => {
      console.error('Failed to terminate tenant lease:', error);
    },
  });
};

// === КОМБІНОВАНІ ХУКИ ===

/**
 * Хук для отримання активних lease tenant'a
 */
export const useTenantActiveLeases = () => {
  return useTenantLeases({
    filters: {
      status: 'active',
    },
  });
};

/**
 * Хук для отримання історії lease tenant'a
 */
export const useTenantLeaseHistory = () => {
  return useTenantLeases({
    filters: {
      status: 'terminated',
    },
  });
};

/**
 * Хук для отримання lease конкретної property
 */
export const useTenantLeasesForProperty = (propertyId: number | undefined) => {
  return useTenantLeases({
    enabled: !!propertyId,
    filters: {
      propertyId,
    },
  });
};

// === HELPER ФУНКЦІЇ ===

/**
 * Перевіряє чи має tenant активні lease
 */
export const useHasActiveLease = (): boolean => {
  const { data: leases = [] } = useTenantActiveLeases();
  return leases.length > 0;
};

/**
 * Отримує кількість активних lease
 */
export const useActiveLeaseCount = (): number => {
  const { data: leases = [] } = useTenantActiveLeases();
  return leases.length;
};

/**
 * Перевіряє чи може tenant завершити lease достроково
 */
export const useCanTerminateLease = (leaseId: number | undefined): boolean => {
  const { data: lease } = useTenantLeaseDetails(leaseId);

  if (!lease || lease.status !== 'active') {
    return false;
  }

  // Тут можна додати додаткову логіку перевірки
  // наприклад, чи дозволено достроково завершення в умовах lease
  return lease.allowEarlyTermination !== false;
};

/**
 * Отримує найближчий lease що закінчується
 */
export const useUpcomingLeaseExpiration = () => {
  const { data: leases = [] } = useTenantActiveLeases();

  const upcomingLease = leases
    .filter((lease) => lease.endDate)
    .sort(
      (a, b) => new Date(a.endDate!).getTime() - new Date(b.endDate!).getTime(),
    )[0];

  return {
    lease: upcomingLease || null,
    daysUntilExpiry: upcomingLease
      ? Math.ceil(
          (new Date(upcomingLease.endDate!).getTime() - new Date().getTime()) /
            (1000 * 60 * 60 * 24),
        )
      : null,
  };
};

// === СТАТИСТИКА ДЛЯ TENANT DASHBOARD ===

/**
 * Хук для отримання статистики lease для tenant dashboard
 */
export const useTenantLeaseStats = () => {
  const { data: allLeases = [], isLoading, error } = useTenantLeases();

  const stats = {
    total: allLeases.length,
    active: allLeases.filter((l) => l.status === 'active').length,
    terminated: allLeases.filter((l) => l.status === 'terminated').length,
    expired: allLeases.filter((l) => l.status === 'expired').length,
    totalMonthlyRent: allLeases
      .filter((l) => l.status === 'active')
      .reduce((sum, l) => sum + l.monthlyRent, 0),
  };

  return {
    stats,
    isLoading,
    error,
  };
};
